import React from 'react';

interface PermissionsTableProps {
  matrix: Record<
    string,
    { view: boolean; create: boolean; edit: boolean; delete: boolean }
  >;
  actions: string[];
  canEdit: boolean;
  togglePermission: (module: string, action: string) => void;
  isCheckboxDisabled: (module: string, action: string) => boolean;
  isDisabled: boolean;
}

const PermissionsTable: React.FC<PermissionsTableProps> = ({
  matrix,
  actions,
  canEdit,
  togglePermission,
  isCheckboxDisabled,
  isDisabled,
}) => {
  // Handles checkbox toggle for a specific module and action
  const handleTogglePermission = (module: string, action: string) => {
    if (!isDisabled && !isCheckboxDisabled(module, action)) {
      togglePermission(module, action);
    }
  };

  return (
    <div className='relative'>
      <table className='w-full text-left mt-4 border border-gray-200'>
        <thead className='bg-gray-100'>
          <tr className='text-[#657584] text-[12px] border-b'>
            <th className='px-4 py-3 font-normal'>MODULE</th>
            {actions.map((a) => (
              <th key={a} className='px-4 py-3 text-center font-normal'>
                {a.toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>

        <tbody style={{ color: '#3F5265', fontSize: '14px' }}>
          {Object.entries(matrix).map(([module, perms]) => (
            <tr key={module} className='border-b hover:bg-gray-50'>
              <td className='px-4 py-2 text-[#3F5265] text-[14px]'>{module}</td>
              {actions.map((a) => {
                const value = perms[a as keyof typeof perms];
                // Always enable checkboxes - no disabled state
                const disabled = isDisabled; // Only disabled when member is temporarily disabled

                return (
                  <td key={a} className='px-4 py-2 text-center'>
                    {canEdit ? (
                      <input
                        type='checkbox'
                        checked={value}
                        onChange={() => handleTogglePermission(module, a)}
                        disabled={disabled}
                        className={`
                          relative h-5 w-5 appearance-none rounded-md border border-gray-300
                          after:absolute after:inset-0 after:flex after:items-center after:justify-center
                          checked:bg-[#00A4BD] checked:border-[#00A4BD] disabled:bg-gray-400 disabled:border-gray-400
                          checked:after:content-['✓'] checked:after:text-white checked:after:font-bold checked:after:text-sm
                          ${disabled
                            ? 'opacity-50 cursor-not-allowed'
                            : 'cursor-pointer'
                          }
                        `}
                      />
                    ) : (
                      <span
                        className={`text-lg font-bold ${value ? 'text-green-600' : 'text-red-500'
                          }`}
                      >
                        {value ? '✓' : '✗'}
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PermissionsTable;